import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Collapse from '@material-ui/core/Collapse'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Typography from '@material-ui/core/Typography'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Grid from '@material-ui/core/Grid'
import { ColorPalette } from 'material-ui-color'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import CountySelect from 'components/general/CountySelect'
import PlacesSelect from 'components/general/PlacesSelect'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import { NotificationManager } from 'react-notifications'
import { paletteOptions, panelWarrantyOptions, warrantyYearOptions0to30 } from 'config/constants/Constants'
import Loader from 'components/general/Loader'
import ReactDropzoneCropper from 'components/general/ReactDropzoneCropper'
import { Storage } from 'aws-amplify'
import config from 'config/api/Config'
import MissingInfoDialog from './MissingInfoDialog'

const useStyles = makeStyles(componentStyles)

const currentYear = new Date().getFullYear()
const years = Array.from({ length: currentYear - 1999 }, (_, i) => (currentYear - i).toString())

function CompanyDetailsCard(props) {
    const classes = { ...useStyles() }
    const [open, setOpen] = useState(true)
    const api = new ProviderAPI()
    const details = props.details || {}

    // Product context (falls back to legacy isSolar-only behaviour when the
    // caller does not supply enabledProducts).
    const enabledProducts = props.enabledProducts || []
    const isSolar = props.isSolar !== false
    const isEV = enabledProducts.includes('EV Charger')
    const isHeatPump = enabledProducts.includes('Heat Pump')
    const isInsulation = enabledProducts.includes('Insulation')
    // Annual Installations applies to Solar, EV Charger and Insulation providers.
    const showAnnualInstallations = isSolar || isEV || isInsulation

    const [imageError, setImageError] = useState(false)
    const [showLogoUrl, setShowLogoUrl] = useState(false)
    const [loading, setLoading] = useState(false)
    const [teamPicError, setTeamPicError] = useState(false)

    // Missing info dialog state
    const [showMissingFields, setShowMissingFields] = useState(false)
    const [missingFields, setMissingFields] = useState([])

    // Reset imageError whenever logo URL changes
    useEffect(() => {
        setImageError(false)
    }, [details.logo])

    function getMissingFields() {
        const {
            yearEstablished,
            annualInstallations,
            panelWarranty,
            performanceWarranty,
            heatPumpWarranty,
            insulationWarranty,
            evChargerWarranty,
            totalInstallations,
            workmanshipWarranty,
            totalStaff,
            officeLocations,
            dedicatedSupport,
            seaiApproved,
            publicLiabilityInsurance,
            professionalIndemnityInsurance,
            logo
            //primaryColor
        } = props.details || {}

        const fields = [
            { key: 'yearEstablished', label: 'Year Established', value: yearEstablished },
            ...(showAnnualInstallations ? [
                { key: 'annualInstallations', label: 'Annual Installations', value: annualInstallations }
            ] : []),
            ...(isSolar ? [
                { key: 'panelWarranty', label: 'Panel Warranty', value: panelWarranty },
                { key: 'performanceWarranty', label: 'Performance Warranty', value: performanceWarranty }
            ] : []),
            ...(isHeatPump ? [
                { key: 'heatPumpWarranty', label: 'Heat Pump Warranty', value: heatPumpWarranty }
            ] : []),
            ...(isInsulation ? [
                { key: 'insulationWarranty', label: 'Insulation Warranty', value: insulationWarranty }
            ] : []),
            ...(isEV ? [
                { key: 'evChargerWarranty', label: 'EV Charger Warranty', value: evChargerWarranty }
            ] : []),
            { key: 'totalInstallations', label: 'Total Installations', value: totalInstallations },
            { key: 'workmanshipWarranty', label: 'Workmanship Warranty', value: workmanshipWarranty },
            { key: 'totalStaff', label: 'Total Staff', value: totalStaff },
            { key: 'officeLocations', label: 'Office Location(s)', value: officeLocations },
            { key: 'dedicatedSupport', label: 'Dedicated Customer Support', value: dedicatedSupport },
            { key: 'seaiApproved', label: 'SEAI Approved', value: seaiApproved },
            { key: 'publicLiabilityInsurance', label: 'Public Liability Insurance', value: publicLiabilityInsurance },
            { key: 'professionalIndemnityInsurance', label: 'Professional Indemnity Insurance', value: professionalIndemnityInsurance },
            { key: 'logo', label: 'Company Logo', value: logo }
            //{ key: 'primaryColor', label: 'Primary Colour', value: primaryColor }
        ]

        return fields.filter(f => {
            if (Array.isArray(f.value)) {
                return f.value.length === 0
            }
            return !f.value || f.value.toString().trim() === ''
        })
    }

    async function saveDetails() {
        // If admin, bypass missing field validation entirely
        let proceed = true
        if (!props.admin) {
            const missing = getMissingFields()
            if (missing.length > 0) {
                setMissingFields(missing)
                setShowMissingFields(true)
                proceed = false
            }
        }
        if (!proceed) return

        setLoading(true)
        const details = props.details || {}

        // Migrate legacy 'Under 100' annualInstallations value on save
        if (details.annualInstallations === 'Under 100') {
            details.annualInstallations = '50 - 100'
        }

        try {
            const updatedCompany = await api.updateProfileDetails(props.companyId, details)
            props.updateCompany(updatedCompany)
            props.updateStage && props.updateStage('previewRecommendation')
            NotificationManager.success('Details saved')
        } catch (e) {
            onError(e)
        }

        setLoading(false)
    }

    async function uploadLogo(image) {
        try {
            let logoPath = 'providers/' + props.companyId + '/logo' 
            if (image) { 
                let imageUploaded = await Storage.put(logoPath, image, {
                    contentType: image.type,
                    level: 'public'
                })
                let newImageUrl = `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${logoPath}`
                props.updateField('profileDetails.logo', newImageUrl)
                setShowLogoUrl(false)
            }
        } catch (e) {
            onError(e)
        }
    }

    async function uploadTeamPicture(image) {
        try {
            let teamPicPath = 'providers/' + props.companyId + '/team-picture'
            if (image) {
                await Storage.put(teamPicPath, image, {
                    contentType: image.type,
                    level: 'public'
                })
                let newImageUrl = `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${teamPicPath}`
                props.updateField('profileDetails.teamPicture', newImageUrl)
                setTeamPicError(false)
            }
        } catch (e) {
            onError(e)
        }
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <>
                        <Box display='flex' style={{justifyContent: 'space-between'}}>
                            <Box>
                                <Box
                                    component={Typography}
                                    variant='h2'
                                    marginBottom='0!important'
                                >
                                    <Box component='span'>Public Profile</Box>
                                </Box>
                                <Box
                                    component='p'
                                    fontSize='.875rem'
                                    marginBottom='0'
                                    marginTop='0'
                                    lineHeight='1.7'
                                    fontWeight='300'
                                >
                                    Information in this section will be available on your public profile
                                </Box>
                            </Box>
                        </Box>
                    </>
                }
                onClick={() => setOpen(o => !o)}
                style={{ cursor: 'pointer' }}
                action={<ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginTop: 4 }} />}
                classes={{ root: classes.cardHeaderRoot }}
            ></CardHeader>
            <Collapse in={open}>
            <CardContent>
                <Grid container>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Company Name</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <OutlinedInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='My Company'
                                    value={props.name}
                                    disabled
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Website URL</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <OutlinedInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='www.mycompany.com'
                                    value={props.url}
                                    disabled
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Company Logo</FormLabel>
                            <Box mt={1} mb={2}>
                                <Typography variant="body2" color="#8898aa">
                                    For best results we recommend using a logo with a white background and cropping a square shape with the logo in the center.
                                </Typography>
                            </Box>
                            {showLogoUrl &&
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <OutlinedInput
                                        autoComplete='off'
                                        type='text'
                                        placeholder='www.mycompany.com/logo'
                                        value={details.logo || ''}
                                        onChange={(e) => props.updateField('profileDetails.logo', e.target.value)}
                                    />
                                </FormControl>
                            }
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} style={{marginBottom: '1rem'}}>
                        {(details.logo && !imageError) &&
                            <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' width='100%'>
                                <img 
                                    className='logo-preview'
                                    src={details.logo + '?v=' + Date.now()} 
                                    onError={() => setImageError(true)} 
                                    onLoad={() => setImageError(false)}
                                    alt='Company Logo'
                                    style={{marginBottom: '1rem'}}
                                />
                            </Box>
                        }
                        {(details.logo && imageError) &&
                            <>
                                <Box display='flex' justifyContent='center' width='100%'>
                                    <p className='red-text'>Error loading logo from URL, please enter a valid URL</p>
                                </Box>
                            </>
                        }
                        <Box display='flex' justifyContent='center' width='100%'>
                            <Box style={{ margin: '0.25rem' }}>
                                <ReactDropzoneCropper
                                    updateImage={(file) => uploadLogo(file)}
                                    companyId={props.companyId}
                                    //button
                                    displayStyle={details.logo ? 'button' : 'box'}
                                    noPreview
                                    flexible
                                />
                            </Box>
                            {/* <Box style={{ margin: '0.25rem' }}>
                                <Button onClick={() => setShowLogoUrl(true)} variant='contained' color='secondary'>
                                    Enter Image URL
                                </Button>
                            </Box> */}
                        </Box>
                    </Grid>
                    {/* Team Picture */}
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormLabel>Team Picture</FormLabel>
                            <Box mt={1} mb={2}>
                                <Typography variant="body2" color="#8898aa">
                                    Upload a team photo to display on your public profile.
                                </Typography>
                            </Box>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} style={{marginBottom: '1rem'}}>
                        {(details.teamPicture && !teamPicError) &&
                            <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' width='100%'>
                                <img 
                                    src={details.teamPicture + '?v=' + Date.now()} 
                                    onError={() => setTeamPicError(true)} 
                                    onLoad={() => setTeamPicError(false)}
                                    alt='Team Picture'
                                    style={{marginBottom: '1rem', maxWidth: '500px', width: '100%', borderRadius: '4px'}}
                                />
                            </Box>
                        }
                        {(details.teamPicture && teamPicError) &&
                            <Box display='flex' justifyContent='center' width='100%'>
                                <p className='red-text'>Error loading team picture</p>
                            </Box>
                        }
                        <Box display='flex' justifyContent='center' width='100%'>
                            <Box style={{ margin: '0.25rem' }}>
                                <ReactDropzoneCropper
                                    updateImage={(file) => uploadTeamPicture(file)}
                                    companyId={props.companyId}
                                    displayStyle={details.teamPicture ? 'button' : 'box'}
                                    noPreview
                                    flexible
                                />
                            </Box>
                        </Box>
                    </Grid>
                    {/* <Grid item xs={12} sm={12}>
                        <FormGroup>
                            <FormLabel>Primary Colour</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                {details.primaryColor ? (
                                    <Box className='selected-color' style={{ backgroundColor: details.primaryColor }} onClick={() => props.updateField('profileDetails.primaryColor',  '')}>
                                        <p>{Object.entries(paletteOptions).find(([key, val]) => val === details.primaryColor)?.[0]}</p>
                                    </Box>
                                ) : (
                                    <ColorPalette 
                                        palette={paletteOptions} 
                                        onSelect={(color, value) => props.updateField('profileDetails.primaryColor', value)}
                                    />
                                )}
                            </FormControl>
                        </FormGroup>
                    </Grid> */}
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Search for your business</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <PlacesSelect 
                                    value={details.placeId}
                                    onChange={(id) => props.updateField('profileDetails.placeId', id)}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Year Established</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.yearEstablished}
                                    onChange={(e) => props.updateField('profileDetails.yearEstablished', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    {years.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                    <MenuItem value='Before 2000'>Before 2000</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    {showAnnualInstallations && (
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Annual Installations</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.annualInstallations === 'Under 100' ? '50 - 100' : details.annualInstallations}
                                    onChange={(e) => props.updateField('profileDetails.annualInstallations', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    <MenuItem value='Up to 50'>Up to 50</MenuItem>
                                    <MenuItem value='50 - 100'>50 - 100</MenuItem>
                                    <MenuItem value='100 - 250'>100 - 250</MenuItem>
                                    <MenuItem value='250 - 500'>250 - 500</MenuItem>
                                    <MenuItem value='500 - 1000'>500 - 1000</MenuItem>
                                    <MenuItem value='1000+'>1000+</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    )}
                    {props.isSolar !== false && (
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Panel Warranty</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.panelWarranty}
                                    onChange={(e) => props.updateField('profileDetails.panelWarranty', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    {panelWarrantyOptions.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    )}
                    {isSolar && (
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Performance Warranty</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.performanceWarranty || ''}
                                    onChange={(e) => props.updateField('profileDetails.performanceWarranty', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    {warrantyYearOptions0to30.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    )}
                    {isHeatPump && (
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Heat Pump Warranty</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.heatPumpWarranty || ''}
                                    onChange={(e) => props.updateField('profileDetails.heatPumpWarranty', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    {warrantyYearOptions0to30.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    )}
                    {isInsulation && (
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Insulation Warranty</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.insulationWarranty || ''}
                                    onChange={(e) => props.updateField('profileDetails.insulationWarranty', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    {warrantyYearOptions0to30.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    )}
                    {isEV && (
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>EV Charger Warranty</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.evChargerWarranty || ''}
                                    onChange={(e) => props.updateField('profileDetails.evChargerWarranty', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    {warrantyYearOptions0to30.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Workmanship Warranty</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.workmanshipWarranty}
                                    onChange={(e) => props.updateField('profileDetails.workmanshipWarranty', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    <MenuItem value='6 months'>6 months</MenuItem>
                                    <MenuItem value='1 year'>1 year</MenuItem>
                                    <MenuItem value='2 years'>2 years</MenuItem>
                                    <MenuItem value='3 years'>3 years</MenuItem>
                                    <MenuItem value='4 years'>4 years</MenuItem>
                                    <MenuItem value='5 years'>5 years</MenuItem>
                                    <MenuItem value='5+ years'> 5+ years</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Total Staff</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <OutlinedInput
                                    autoComplete='off'
                                    type='number'
                                    placeholder='10'
                                    value={details.totalStaff}
                                    onChange={(e) => props.updateField('profileDetails.totalStaff', e.target.value)}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Total Installations</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <OutlinedInput
                                    autoComplete='off'
                                    type='number'
                                    placeholder='500'
                                    value={details.totalInstallations || ''}
                                    onChange={(e) => props.updateField('profileDetails.totalInstallations', e.target.value)}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Office Location(s)</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <CountySelect 
                                    counties={details.officeLocations || []}
                                    updateCounties={(counties) => props.updateField('profileDetails.officeLocations', counties)}
                                    allOption={false}
                                    multiple={true}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Dedicated Customer Support</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.dedicatedSupport}
                                    onChange={(e) => props.updateField('profileDetails.dedicatedSupport', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    <MenuItem value='Yes'>Yes</MenuItem>
                                    <MenuItem value='No'>No</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>SEAI Approved</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.seaiApproved}
                                    onChange={(e) => props.updateField('profileDetails.seaiApproved', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    <MenuItem value='Yes'>Yes</MenuItem>
                                    <MenuItem value='No'>No</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Public Liability Insurance</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.publicLiabilityInsurance}
                                    onChange={(e) => props.updateField('profileDetails.publicLiabilityInsurance', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    <MenuItem value='Yes'>Yes</MenuItem>
                                    <MenuItem value='No'>No</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Professional Indemnity Insurance</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.professionalIndemnityInsurance}
                                    onChange={(e) => props.updateField('profileDetails.professionalIndemnityInsurance', e.target.value)}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    <MenuItem value='Yes'>Yes</MenuItem>
                                    <MenuItem value='No'>No</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>

                    {/* Reviews Configuration - Admin only */}
                    {props.admin && <>
                    <Grid item xs={12}>
                        <Box mt={4} mb={1}>
                            <Typography variant='h3' style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                Reviews Configuration
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <FormLabel>Enable Reviews</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    multiple={false}
                                    value={details.reviewsEnabled === true ? 'Yes' : 'No'}
                                    onChange={(e) => props.updateField('profileDetails.reviewsEnabled', e.target.value === 'Yes')}
                                    input={<OutlinedInput />}
                                    style={{marginBottom: '0.5rem'}}
                                >
                                    <MenuItem value='Yes'>Yes</MenuItem>
                                    <MenuItem value='No'>No</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    {details.reviewsEnabled === true && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <FormGroup>
                                    <FormLabel>Reviews Platform</FormLabel>
                                    <FormControl
                                        variant='filled'
                                        component={Box}
                                        width='100%'
                                    >
                                        <Select
                                            multiple={false}
                                            value={details.reviewsPlatform || ''}
                                            onChange={(e) => props.updateField('profileDetails.reviewsPlatform', e.target.value)}
                                            input={<OutlinedInput />}
                                            style={{marginBottom: '0.5rem'}}
                                        >
                                            <MenuItem value='trustpilot'>Trustpilot</MenuItem>
                                            <MenuItem value='google'>Google</MenuItem>
                                        </Select>
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormGroup>
                                    <FormLabel>Elfsight Embed Code</FormLabel>
                                    <FormControl
                                        variant='filled'
                                        component={Box}
                                        width='100%'
                                    >
                                        <OutlinedInput
                                            autoComplete='off'
                                            type='text'
                                            placeholder='elfsight-app-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                                            value={details.reviewsElfsightCode || ''}
                                            onChange={(e) => props.updateField('profileDetails.reviewsElfsightCode', e.target.value)}
                                        />
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormGroup>
                                    <FormLabel>Current Rating</FormLabel>
                                    <FormControl
                                        variant='filled'
                                        component={Box}
                                        width='100%'
                                    >
                                        <OutlinedInput
                                            autoComplete='off'
                                            type='text'
                                            placeholder='4.8'
                                            value={details.reviewsRating || ''}
                                            onChange={(e) => props.updateField('profileDetails.reviewsRating', e.target.value)}
                                        />
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormGroup>
                                    <FormLabel>Number of Reviews</FormLabel>
                                    <FormControl
                                        variant='filled'
                                        component={Box}
                                        width='100%'
                                    >
                                        <OutlinedInput
                                            autoComplete='off'
                                            type='text'
                                            placeholder='1117'
                                            value={details.reviewsCount || ''}
                                            onChange={(e) => props.updateField('profileDetails.reviewsCount', e.target.value)}
                                        />
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormGroup>
                                    <FormLabel>Read All Reviews Button Text</FormLabel>
                                    <FormControl
                                        variant='filled'
                                        component={Box}
                                        width='100%'
                                    >
                                        <OutlinedInput
                                            autoComplete='off'
                                            type='text'
                                            placeholder='Read 1,117 Customer Reviews on Trustpilot'
                                            value={details.reviewsButtonText || ''}
                                            onChange={(e) => props.updateField('profileDetails.reviewsButtonText', e.target.value)}
                                        />
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormGroup>
                                    <FormLabel>Read All Reviews Button Link</FormLabel>
                                    <FormControl
                                        variant='filled'
                                        component={Box}
                                        width='100%'
                                    >
                                        <OutlinedInput
                                            autoComplete='off'
                                            type='text'
                                            placeholder='https://www.trustpilot.com/review/example.ie'
                                            value={details.reviewsButtonLink || ''}
                                            onChange={(e) => props.updateField('profileDetails.reviewsButtonLink', e.target.value)}
                                        />
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                        </>
                    )}
                    </>}

                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='flex-end' width='100%'>
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={loading}
                                onClick={saveDetails}
                            >
                                {!loading ? (
                                    <>{(props.admin || props.profile) ? 'Save' : 'Save & Continue'}</>
                                ) : (
                                    <Loader height={12} />
                                )}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
            </Collapse>
            <MissingInfoDialog
                open={showMissingFields}
                onClose={() => setShowMissingFields(false)}
                missingFields={missingFields}
            />
        </Card>
    );
}

export default CompanyDetailsCard